import React, { useState } from "react";
import { MessageSquare, Mail, Link2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * "Send this to someone" widget. Shown after a buyer activates so they can
 * pass the course to one person they love in 5 seconds.
 *
 * Pre-fills SMS + email with copy that reads like a personal recommendation
 * (not a corporate share blast). Includes a copy-link button for any other channel.
 */
const ShareWithFriend = ({
    url = "https://wearemindworks.org/daily-battle-drill",
    senderFirstName = "",
}) => {
    const [copied, setCopied] = useState(false);

    const personalCopy =
        `Hey — I just grabbed The Daily Battle Drill from MindWorks. It's a 4-step routine for taking back the war in your mind. Real neuroscience, real biblical truth. Normally $97, they're doing $7 through Christmas only. Thought of you. ${url}`;
    const emailSubject = "Thought of you — this could help";
    const emailBody = personalCopy;
    const smsHref = `sms:?&body=${encodeURIComponent(personalCopy)}`;
    const mailHref = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(personalCopy);
            setCopied(true);
            toast.success("Copied. Now paste it to someone who needs this.");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Couldn't copy — try long-pressing the link.");
        }
    };

    return (
        <div
            className="border border-white/10 bg-[#0a0a0a] p-6 sm:p-8 mt-10"
            data-testid="share-with-friend"
        >
            <div className="font-stencil text-[10px] tracking-[0.3em] text-red-600 mb-3">
                [ ONE MORE THING ]
            </div>
            <div className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-tight mb-3">
                {senderFirstName ? `${senderFirstName}, this could change someone's life.` : "This could change someone's life."}
            </div>
            <p className="text-white/65 text-sm leading-relaxed mb-6 max-w-xl">
                Think of <span className="text-white font-semibold">one person</span> in your life right now who's fighting a battle in their head. Stay up at night for them? Then send them this. The $7 summer price won&apos;t last — but your friendship will.
            </p>
            <div className="flex flex-wrap gap-3">
                <a
                    href={smsHref}
                    className="btn-primary inline-flex"
                    data-testid="share-sms"
                >
                    <MessageSquare size={16} /> Text it to them
                </a>
                <a
                    href={mailHref}
                    className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 hover:border-red-600 text-white/85 hover:text-white font-display tracking-[0.15em] text-sm uppercase transition-colors"
                    data-testid="share-email"
                >
                    <Mail size={16} /> Email it
                </a>
                <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 hover:border-red-600 text-white/85 hover:text-white font-display tracking-[0.15em] text-sm uppercase transition-colors"
                    data-testid="share-copy"
                >
                    {copied ? <Check size={16} /> : <Link2 size={16} />}
                    {copied ? "Copied" : "Copy message"}
                </button>
            </div>
        </div>
    );
};

export default ShareWithFriend;
